/**
 * AUVersionCompareModal — AU (TGA PI) main-label version compare, at full feature
 * parity with the EU historical compare (EuHistoricalCompare.tsx). AU Product
 * Information is a single-language English native-text PDF, so this is the EU-style
 * two-pane PDF compare (page images + red/green word boxes + figure add/remove
 * overlays) — NOT the JP bilingual-HTML path.
 *
 * Ported from EuHistoricalCompare, AU-scoped: it keeps the section rail, count
 * summary + verdict filters, next/prev change stepper + keyboard nav, synchronized
 * scroll, colour legend, loading skeleton, figure (image) add/remove highlighting
 * with a per-section figure stepper, and the quarantine fallback. It DROPS the two
 * EU-only pieces that don't apply to AU: the multi-part hierarchical tree (AU PI is
 * a single document / single part) and the CEPI citation-block landing (AU isn't
 * wired into CEPI). The EU prod component is left untouched.
 *
 * Data (via authApi):
 *   getDrugAuVersions(drugId)              → the version timeline
 *   getDrugAuVersionDiff(drugId, from, to) → stored diff payload:
 *     { from_version, to_version, from/to_revision_date, from/to_tga_code,
 *       totals, sections, highlights:{old,new}, attribution,
 *       old_pages:[{page,w,h,img}], new_pages:[...] }
 * The diff is stored per ADJACENT pair (au_version_diff_phase_b); a non-adjacent
 * pick returns 404 and we show a friendly "no stored comparison" message.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authApi } from '../services/authApi';

interface AuVersion {
  version_number: number;
  tga_code: string | null;
  revision_date: string | null;
  is_current: boolean;
}
type Verdict = 'unchanged' | 'modified' | 'added' | 'removed';
interface FigMark { op: string; page: number; y: number; box: [number, number, number, number] }
interface Section {
  section_number: string; title: string; verdict: Verdict;
  change_count: number;
  words_added: number; words_removed: number;
  old_page?: number | null; new_page?: number | null;
  old_y?: number | null; new_y?: number | null;
  figures_added?: number; figures_removed?: number;
  fig_old_page?: number | null; fig_old_y?: number | null;
  fig_new_page?: number | null; fig_new_y?: number | null;
  fig_marks?: FigMark[];
  summary_add?: string; summary_del?: string;
}
interface HL { page: number; box: [number, number, number, number]; kind: string }
interface PageInfo { page: number; w: number | null; h: number | null; img: string | null }
interface CitedSpan { page: number; box?: [number, number, number, number] }
interface Attribution { status: string; flags?: string[] }
interface AuDiff {
  from_version: number; to_version: number;
  from_revision_date: string | null; to_revision_date: string | null;
  from_tga_code: string | null; to_tga_code: string | null;
  totals: {
    changed_sections: number;
    sections_added: string[]; sections_removed: string[]; sections_modified: string[];
    total_changes: number; figures_added: number; figures_removed: number;
  };
  sections: Section[];
  attribution?: Attribution;
  highlights: { old: HL[]; new: HL[] };
  old_pages: PageInfo[]; new_pages: PageInfo[];
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt.getTime())) return d;
  return `${MON[dt.getMonth()]} ${String(dt.getDate()).padStart(2, '0')} ${dt.getFullYear()}`;
}

function ImageGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <circle cx="8.5" cy="8.5" r="1.6" />
      <path d="M21 15.5 16 10.5 5 21.5" />
    </svg>
  );
}

const VERDICT_META: Record<Verdict, { label: string; full: string; pill: string; count: string; border: string }> = {
  added:     { label: 'Added',     full: 'New section added',        pill: 'bg-emerald-100 text-emerald-700', count: 'text-emerald-600', border: 'border-l-emerald-400' },
  modified:  { label: 'Modified',  full: 'Section content modified', pill: 'bg-amber-100 text-amber-700',     count: 'text-amber-600',   border: 'border-l-amber-400' },
  removed:   { label: 'Deleted',   full: 'Section deleted',          pill: 'bg-rose-100 text-rose-700',       count: 'text-rose-600',    border: 'border-l-rose-400' },
  unchanged: { label: 'Unchanged', full: 'No change',                pill: '',                                count: 'text-slate-400',   border: 'border-l-transparent' },
};
const VERDICT_ORDER: Verdict[] = ['added', 'modified', 'removed', 'unchanged'];
type Filter = 'all' | 'figures' | Verdict;
const FILTERS: [Filter, string][] = [
  ['all', 'All'], ['figures', 'Figures'], ['added', 'Added'],
  ['modified', 'Modified'], ['removed', 'Deleted'], ['unchanged', 'Unchanged'],
];
const hasFigChange = (s: Section) => (s.figures_added || 0) > 0 || (s.figures_removed || 0) > 0;
const LOAD_MSGS = ['Loading versions…', 'Computing changes…', 'Rendering pages…'];

function groupByPage(hls: HL[]): Map<number, HL[]> {
  const m = new Map<number, HL[]>();
  for (const h of hls) { const a = m.get(h.page) || []; a.push(h); m.set(h.page, a); }
  return m;
}

function Pane({ side, pages, hls, cited, show, paneRef, viewing, page, tag }: {
  side: 'old' | 'new'; pages: PageInfo[]; hls: HL[]; cited: CitedSpan[]; show: boolean;
  paneRef: React.RefObject<HTMLDivElement>; viewing: string; page: number | null; tag: string;
}) {
  const byPage = groupByPage(hls);
  const citedByPage = new Map<number, CitedSpan[]>();
  for (const c of cited) {
    if (!c.box) continue;
    const a = citedByPage.get(c.page) || []; a.push(c); citedByPage.set(c.page, a);
  }
  return (
    <div ref={paneRef} className="relative flex-1 overflow-y-auto bg-slate-100" style={{ minWidth: 0 }}>
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-1.5 text-[11px] flex justify-between gap-2 items-center">
        <span className="flex items-center gap-2 shrink-0">
          <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${side === 'old' ? 'bg-slate-100 text-slate-500' : 'bg-indigo-100 text-indigo-700'}`}>
            {side === 'old' ? 'Older' : 'Newer'}
          </span>
          {tag && <span className="font-medium text-slate-700">{tag}</span>}
        </span>
        <span className="truncate text-slate-500">{viewing ? `${viewing}${page ? ` · p${page}` : ''}` : ''}</span>
      </div>
      <div className="px-4 py-3">
        {pages.length === 0 && (
          <div className="text-sm text-slate-400 text-center pt-10">No pages to display.</div>
        )}
        {pages.map((p) => (
          <div key={p.page} data-page={p.page} className="relative mx-auto mb-4 rounded-lg shadow-sm ring-1 ring-slate-200 bg-white"
               style={{ width: '100%', maxWidth: 700, aspectRatio: p.w && p.h ? `${p.w} / ${p.h}` : '1 / 1.414' }}>
            {p.img && (
              <img src={p.img} loading="lazy" alt={`page ${p.page}`} className="block w-full h-full select-none rounded-lg" draggable={false} />
            )}
            {show && (byPage.get(p.page) || []).map((h, i) => {
              const isImg = h.kind === 'img_add' || h.kind === 'img_del';
              const isAdd = h.kind === 'add' || h.kind === 'img_add';
              return (
                <div key={i} className="absolute pointer-events-none" style={{
                  left: `${h.box[0] * 100}%`, top: `${h.box[1] * 100}%`,
                  width: `${(h.box[2] - h.box[0]) * 100}%`, height: `${(h.box[3] - h.box[1]) * 100}%`,
                  backgroundColor: isImg ? (isAdd ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)')
                    : (isAdd ? 'rgba(34,197,94,0.30)' : 'rgba(239,68,68,0.30)'),
                  border: isImg ? `2px dashed ${isAdd ? 'rgba(22,163,74,0.9)' : 'rgba(220,38,38,0.9)'}` : undefined,
                  boxShadow: isImg ? undefined
                    : (isAdd ? 'inset 0 0 0 1px rgba(22,163,74,0.65)' : 'inset 0 0 0 1px rgba(220,38,38,0.65)'),
                  borderRadius: isImg ? 4 : 2,
                }}>
                  {isImg && (
                    <span className={`absolute -top-3 left-0 flex items-center gap-1 text-[10px] font-medium leading-none px-1.5 py-1 rounded-full shadow-sm text-white ${isAdd ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                      <ImageGlyph className="w-3 h-3" /> {isAdd ? 'Figure added' : 'Figure removed'}
                    </span>
                  )}
                </div>
              );
            })}
            {show && (citedByPage.get(p.page) || []).map((c, i) => (
              <div key={`cited-${i}`} data-testid="cited-span" className="absolute pointer-events-none" style={{
                left: `${c.box![0] * 100}%`, top: `${c.box![1] * 100}%`,
                width: `${(c.box![2] - c.box![0]) * 100}%`, height: `${(c.box![3] - c.box![1]) * 100}%`,
                border: '2px solid rgba(79,70,229,0.95)', borderRadius: 3,
                boxShadow: '0 0 0 2px rgba(79,70,229,0.25)', backgroundColor: 'rgba(99,102,241,0.12)',
              }} />
            ))}
            <div className="absolute top-1 right-2 text-[10px] text-slate-400">p{p.page}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareSkeleton({ msg }: { msg: string }) {
  return (
    <div className="relative flex flex-1 min-h-0">
      <div className="flex flex-1 min-h-0 animate-pulse">
        <div className="w-[360px] shrink-0 border-r bg-white p-3 space-y-2">
          <div className="h-6 bg-slate-100 rounded" />
          <div className="h-6 bg-slate-100 rounded w-2/3" />
          {Array.from({ length: 9 }).map((_, i) => (<div key={i} className="h-6 bg-gray-100 rounded" />))}
        </div>
        {['old', 'new'].map((s) => (
          <div key={s} className="flex-1 bg-gray-100 p-4 space-y-4 overflow-hidden">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="mx-auto bg-white rounded shadow" style={{ width: '100%', maxWidth: 700, aspectRatio: '0.72' }} />
            ))}
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="flex items-center gap-3 bg-white/95 border rounded-lg px-5 py-3 shadow-sm">
          <span className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-600">{msg}</span>
        </div>
      </div>
    </div>
  );
}

function SectionRow({ s, isSel, onSelect }: { s: Section; isSel: boolean; onSelect: (s: Section) => void }) {
  const m = VERDICT_META[s.verdict];
  const changed = s.verdict !== 'unchanged';
  return (
    <div className={`border-b border-slate-50 border-l-2 ${m.border} transition-colors ${isSel ? 'bg-indigo-50/70' : 'hover:bg-slate-50'}`}>
      <button onClick={() => onSelect(s)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <span className="text-[11px] tabular-nums w-9 shrink-0 text-slate-400">{s.section_number}</span>
        <span className={`flex-1 truncate ${changed ? 'text-slate-700' : 'text-slate-400'}`}>{s.title}</span>
        {changed && (s.words_added > 0 || s.words_removed > 0) && (
          <span className="text-[10px] tabular-nums shrink-0">
            {s.words_added > 0 && <span className="text-emerald-600">+{s.words_added}</span>}
            {s.words_removed > 0 && <span className="text-rose-500 ml-1">−{s.words_removed}</span>}
          </span>
        )}
        {changed && hasFigChange(s) && (
          <span className="flex items-center gap-0.5 text-[10px] tabular-nums shrink-0" title="figures added / removed">
            <ImageGlyph className="w-3 h-3 text-slate-400" />
            {(s.figures_added || 0) > 0 && <span className="text-emerald-600">+{s.figures_added}</span>}
            {(s.figures_removed || 0) > 0 && <span className="text-rose-500 ml-0.5">−{s.figures_removed}</span>}
          </span>
        )}
        {(s.verdict === 'added' || s.verdict === 'removed') &&
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${m.pill}`}>{m.full}</span>}
      </button>
      {isSel && changed && (
        <div className="px-3 pb-3 pt-0.5 text-[11px] space-y-1">
          {s.summary_add && <div className="text-emerald-700 truncate">+ {s.summary_add}</div>}
          {s.summary_del && <div className="text-rose-700 truncate line-through decoration-rose-300">− {s.summary_del}</div>}
          <div className="text-slate-500">
            Pages <b className="text-slate-700">p{s.old_page ?? '—'}</b> → <b className="text-slate-700">p{s.new_page ?? '—'}</b>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AUVersionCompareModal({ drugId, brand, versions, onClose }: {
  drugId: number; brand: string; versions: AuVersion[]; onClose: () => void;
}) {
  // newest-first dropdown options; oldest = V-lowest.
  const ordered = useMemo(
    () => [...versions].sort((a, b) => a.version_number - b.version_number), [versions]);
  const current = useMemo(
    () => ordered.find((v) => v.is_current) ?? ordered[ordered.length - 1], [ordered]);
  const oldest = ordered[0];

  const [oldV, setOldV] = useState<number>(oldest?.version_number ?? 0);
  const [newV, setNewV] = useState<number>(current?.version_number ?? 0);
  const [diff, setDiff] = useState<AuDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState('');
  const [showChanges, setShowChanges] = useState(true);
  const [syncScroll, setSyncScroll] = useState(true);
  const [loadStage, setLoadStage] = useState(0);
  const [citedOld, setCitedOld] = useState<CitedSpan[]>([]);
  const [citedNew, setCitedNew] = useState<CitedSpan[]>([]);
  const [figIdx, setFigIdx] = useState(-1);

  const oldPane = useRef<HTMLDivElement>(null);
  const newPane = useRef<HTMLDivElement>(null);
  const anchor = useRef<{ old: number; new: number } | null>(null);
  const suspendSync = useRef(false);
  const suspendTimer = useRef<number>(0);

  useEffect(() => {
    if (!oldV || !newV || oldV >= newV) { setDiff(null); return; }
    let alive = true;
    setLoading(true); setError(null); setDiff(null); setSelected('');
    setCitedOld([]); setCitedNew([]);
    (async () => {
      try {
        const d = await authApi.getDrugAuVersionDiff(drugId, oldV, newV);
        if (alive) setDiff(d as AuDiff);
      } catch (e: unknown) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load diff');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [drugId, oldV, newV]);

  useEffect(() => {
    if (!loading) { setLoadStage(0); return; }
    const t = setInterval(() => setLoadStage((s) => Math.min(s + 1, LOAD_MSGS.length - 1)), 2500);
    return () => clearInterval(t);
  }, [loading]);

  const computeTarget = useCallback(
    (ref: React.RefObject<HTMLDivElement>, page: number | null, y: number | null): number | null => {
      if (!ref.current || !page) return null;
      const el = ref.current.querySelector<HTMLElement>(`[data-page="${page}"]`);
      if (!el) return null;
      // Clamp to the pane's max scroll: the browser stops there anyway, and the sync
      // anchor must equal where the pane actually lands — an unclamped anchor makes
      // the driver drag the other pane back by the overshoot once suspend lifts.
      return Math.max(0, Math.min(ref.current.scrollHeight - ref.current.clientHeight,
        el.offsetTop + (y || 0) * el.clientHeight - 44));
    }, []);
  const landBoth = useCallback(
    (op: number | null, oy: number | null, np: number | null, ny: number | null) => {
      const a = oldPane.current, b = newPane.current;
      const ot = computeTarget(oldPane, op, oy);
      const nt = computeTarget(newPane, np, ny);
      suspendSync.current = true;
      if (a && ot != null) a.scrollTo({ top: ot, behavior: 'smooth' });
      if (b && nt != null) b.scrollTo({ top: nt, behavior: 'smooth' });
      anchor.current = { old: ot != null ? ot : (a?.scrollTop ?? 0), new: nt != null ? nt : (b?.scrollTop ?? 0) };
      window.clearTimeout(suspendTimer.current);
      // Keep the driver muted until BOTH panes have stopped moving, then re-anchor on
      // where they actually rest. A fixed 550 ms delay lifted it mid-animation on long
      // jumps, and the driver's first write cancelled both smooth scrolls halfway.
      // Re-anchoring on the resting positions also covers a user-interrupted landing.
      let lastOld = a?.scrollTop ?? 0, lastNew = b?.scrollTop ?? 0, still = 0, waited = 0;
      const settle = () => {
        const curOld = a?.scrollTop ?? 0, curNew = b?.scrollTop ?? 0;
        still = curOld === lastOld && curNew === lastNew ? still + 1 : 0;
        lastOld = curOld; lastNew = curNew; waited += 50;
        if (still < 3 && waited < 3000) { suspendTimer.current = window.setTimeout(settle, 50); return; }
        anchor.current = { old: curOld, new: curNew };
        suspendSync.current = false;
      };
      suspendTimer.current = window.setTimeout(settle, 50);
    }, [computeTarget]);

  const landFigure = (s: Section, i: number) => {
    const marks = s.fig_marks || [];
    if (!marks.length) { setFigIdx(-1); landBoth(s.old_page ?? null, s.old_y ?? 0, s.new_page ?? null, s.new_y ?? 0); return; }
    const n = marks.length;
    const j = ((i % n) + n) % n;
    const m = marks[j]!;
    setFigIdx(j);
    const span: CitedSpan[] = [{ page: m.page, box: m.box }];
    if (m.op === 'removed') {
      setCitedOld(span); setCitedNew([]);
      landBoth(m.page, m.y, s.new_page ?? null, s.new_y ?? 0);
    } else {
      setCitedNew(span); setCitedOld([]);
      landBoth(s.old_page ?? null, s.old_y ?? 0, m.page, m.y);
    }
  };
  const select = (s: Section) => {
    setSelected(s.section_number);
    setCitedOld([]); setCitedNew([]);
    if (filter === 'figures' && hasFigChange(s) && s.fig_marks?.length) {
      landFigure(s, 0);
    } else {
      setFigIdx(-1);
      landBoth(s.old_page ?? null, s.old_y ?? 0, s.new_page ?? null, s.new_y ?? 0);
    }
  };

  const sections = diff?.sections || [];
  const quarantined = !!diff?.attribution && diff.attribution.status !== 'ok';
  const counts = useMemo(() => {
    const c: Record<Verdict, number> = { added: 0, modified: 0, removed: 0, unchanged: 0 };
    for (const s of sections) c[s.verdict]++;
    return c;
  }, [sections]);
  const figTotals = useMemo(() => {
    let sec = 0, added = 0, removed = 0;
    for (const s of sections) {
      const a = s.figures_added || 0, r = s.figures_removed || 0;
      if (a > 0 || r > 0) sec++;
      added += a; removed += r;
    }
    return { sections: sec, added, removed };
  }, [sections]);
  const visible = useMemo(
    () => sections.filter((s) =>
      filter === 'all' ? true : filter === 'figures' ? hasFigChange(s) : s.verdict === filter),
    [sections, filter]);
  const changed = useMemo(() => sections.filter((s) => s.verdict !== 'unchanged'), [sections]);

  const step = useCallback((dir: 1 | -1) => {
    if (!changed.length) return;
    const i = changed.findIndex((s) => s.section_number === selected);
    const nx = changed[(i + dir + changed.length) % changed.length];
    if (nx) select(nx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changed, selected]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'SELECT' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'j') step(1);
      if (e.key === 'ArrowLeft' || e.key === 'k') step(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [step]);

  useEffect(() => {
    if (!syncScroll || !diff) return;
    const a = oldPane.current, b = newPane.current;
    if (!a || !b) return;
    if (!anchor.current) anchor.current = { old: a.scrollTop, new: b.scrollTop };
    const ignore = { a: 0, b: 0 };
    const drive = (src: HTMLDivElement, dst: HTMLDivElement, srcSide: 'old' | 'new', dstSide: 'old' | 'new', dstKey: 'a' | 'b') => {
      if (suspendSync.current || !anchor.current) return;
      const delta = src.scrollTop - anchor.current[srcSide];
      const target = Math.max(0, Math.min(anchor.current[dstSide] + delta, dst.scrollHeight - dst.clientHeight));
      if (Math.abs(dst.scrollTop - target) > 1) { ignore[dstKey]++; dst.scrollTop = target; }
    };
    const onA = () => { if (ignore.a > 0) { ignore.a--; return; } drive(a, b, 'old', 'new', 'b'); };
    const onB = () => { if (ignore.b > 0) { ignore.b--; return; } drive(b, a, 'new', 'old', 'a'); };
    a.addEventListener('scroll', onA, { passive: true });
    b.addEventListener('scroll', onB, { passive: true });
    return () => { a.removeEventListener('scroll', onA); b.removeEventListener('scroll', onB); };
  }, [syncScroll, diff]);
  useEffect(() => {
    anchor.current = null;
    // Stop a pending landing's settle poll when the pair changes or we unmount.
    return () => { window.clearTimeout(suspendTimer.current); suspendSync.current = false; };
  }, [diff]);

  const sel = sections.find((s) => s.section_number === selected) || null;
  const stepFig = (dir: 1 | -1) => {
    const marks = sel?.fig_marks || [];
    if (!sel || !marks.length) return;
    landFigure(sel, figIdx < 0 ? (dir === 1 ? 0 : marks.length - 1) : figIdx + dir);
  };

  // dropdown options — newest first, "PI - V{n} - date"
  const verOpts = ordered
    .map((v) => ({ vn: v.version_number, label: `PI - V${v.version_number} - ${fmtDate(v.revision_date)}` }))
    .reverse();
  const oldVer = ordered.find((v) => v.version_number === oldV);
  const newVer = ordered.find((v) => v.version_number === newV);
  const oldTag = oldVer ? `PI V${oldVer.version_number} · ${fmtDate(oldVer.revision_date)}` : '';
  const newTag = newVer ? `PI V${newVer.version_number} · ${fmtDate(newVer.revision_date)}` : '';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[1500px] max-h-[94vh] overflow-hidden flex flex-col text-sm"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-2.5 border-b border-slate-200 bg-white shrink-0 flex-wrap">
          <span className="font-semibold text-slate-800 truncate">
            {brand}<span className="font-normal text-slate-400"> · historical AU Product Information</span>
          </span>
          {ordered.length > 0 && (
            <div className="flex items-center gap-2 ml-1">
              <select className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
                      value={oldV} onChange={(e) => setOldV(Number(e.target.value))}>
                {verOpts.map((o) => (
                  <option key={o.vn} value={o.vn} disabled={o.vn >= newV}>{o.label}</option>
                ))}
              </select>
              <span className="text-slate-300">→</span>
              <select className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
                      value={newV} onChange={(e) => setNewV(Number(e.target.value))}>
                {verOpts.map((o) => (
                  <option key={o.vn} value={o.vn} disabled={o.vn <= oldV}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            {showChanges && (
              <div className="hidden md:flex items-center gap-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-400/60 ring-1 ring-emerald-500" /> Added in newer</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-400/60 ring-1 ring-rose-500" /> Removed from older</span>
              </div>
            )}
            {sel && sel.fig_marks && sel.fig_marks.length > 0 && (
              <div className="flex items-center gap-1 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-1.5 py-1 text-xs text-amber-800"
                   title="Step through each changed figure in this section">
                <ImageGlyph className="w-3.5 h-3.5" />
                <button onClick={() => stepFig(-1)} className="px-1 rounded hover:bg-amber-100" title="Previous figure">◀</button>
                <span className="tabular-nums select-none">
                  {figIdx < 0 ? `${sel.fig_marks.length} fig` : `${figIdx + 1} / ${sel.fig_marks.length}`}
                </span>
                <button onClick={() => stepFig(1)} className="px-1 rounded hover:bg-amber-100" title="Next figure">▶</button>
              </div>
            )}
            <button onClick={() => setSyncScroll((v) => {
                      const nv = !v;
                      if (nv && oldPane.current && newPane.current)
                        anchor.current = { old: oldPane.current.scrollTop, new: newPane.current.scrollTop };
                      return nv;
                    })}
                    title="Scroll both documents together, anchored where you are"
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition-colors ${syncScroll
                      ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'bg-slate-100 text-slate-500 hover:text-slate-700'}`}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 8l-4 4 4 4" /><path d="M17 8l4 4-4 4" /><line x1="3" y1="12" x2="21" y2="12" />
              </svg>
              Sync scroll
            </button>
            <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
              <button onClick={() => setShowChanges(false)} className={`rounded-md px-3 py-1 transition-colors ${!showChanges ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Document</button>
              <button onClick={() => setShowChanges(true)} className={`rounded-md px-3 py-1 transition-colors ${showChanges ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Changes</button>
            </div>
            <button onClick={onClose} className="rounded-md w-7 h-7 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors text-lg leading-none" title="Close">×</button>
          </div>
        </div>

        {error && <div className="px-5 py-2 text-rose-600 bg-rose-50 shrink-0">{error}</div>}
        {loading && <CompareSkeleton msg={LOAD_MSGS[loadStage] || LOAD_MSGS[0]!} />}

        {diff && !loading && (
          <div className="flex flex-1 min-h-0">
            <div className="w-[360px] shrink-0 flex flex-col border-r border-slate-200 bg-white">
              {quarantined ? (
                <div className="flex-1 overflow-y-auto px-4 py-5 text-[13px] leading-relaxed text-amber-900">
                  <div className="flex items-center gap-2 font-semibold text-amber-700 mb-2">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Section attribution uncertain
                  </div>
                  <p className="text-amber-800">
                    This older label has no reliable machine-readable text layer, so changes can’t be attributed to individual sections and automated highlights aren’t available. Both documents remain available on the right for side-by-side review.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 border-b border-slate-100">
                    <span className="font-semibold text-slate-700">{sections.length} sections total</span>
                    {VERDICT_ORDER.filter((v) => v !== 'unchanged').map((v) => counts[v] > 0 && (
                      <span key={v} className={VERDICT_META[v].count}>{counts[v]} {VERDICT_META[v].label.toLowerCase()}</span>
                    ))}
                    {figTotals.added > 0 && (
                      <span className="text-emerald-600">{figTotals.added} figure{figTotals.added > 1 ? 's' : ''} added</span>
                    )}
                    {figTotals.removed > 0 && (
                      <span className="text-rose-500">{figTotals.removed} figure{figTotals.removed > 1 ? 's' : ''} removed</span>
                    )}
                    <span className="text-slate-400">{counts.unchanged} unchanged</span>
                  </div>
                  <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-100">
                    {FILTERS.map(([f, lbl]) => {
                      if (f === 'figures' && figTotals.sections === 0) return null;
                      const isFig = f === 'figures';
                      return (
                        <button key={f} onClick={() => setFilter(f)}
                                className={`text-[11px] px-2.5 py-0.5 rounded-full transition-colors ${filter === f
                                  ? (isFig ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white')
                                  : (isFig ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-50 text-slate-500 hover:bg-slate-100')}`}>
                          {lbl}{isFig ? ` · ${figTotals.sections} section${figTotals.sections > 1 ? 's' : ''}` : ''}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {visible.map((s) => (
                      <SectionRow key={s.section_number} s={s} isSel={selected === s.section_number} onSelect={select} />
                    ))}
                    {visible.length === 0 && (
                      <div className="px-3 py-6 text-center text-gray-400 text-xs">Nothing matches this filter.</div>
                    )}
                  </div>
                  {changed.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-slate-100 bg-slate-50/70 shrink-0">
                        <button onClick={() => step(-1)} className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors">← Previous</button>
                        <button onClick={() => step(1)} className="flex-1 px-2 py-1.5 rounded-md bg-slate-800 text-white text-xs font-medium hover:bg-slate-900 transition-colors">Next change →</button>
                      </div>
                      <div className="px-3 pb-2 text-[10px] text-slate-300 text-center shrink-0">← / → or j / k to navigate</div>
                    </>
                  )}
                </>
              )}
            </div>

            <Pane side="old" pages={diff.old_pages} hls={diff.highlights.old} cited={citedOld} show={showChanges && !quarantined} paneRef={oldPane}
                  viewing={sel ? `§${sel.section_number} ${sel.title}` : ''} page={sel?.old_page ?? null} tag={oldTag} />
            <div className="w-px bg-slate-200" />
            <Pane side="new" pages={diff.new_pages} hls={diff.highlights.new} cited={citedNew} show={showChanges && !quarantined} paneRef={newPane}
                  viewing={sel ? `§${sel.section_number} ${sel.title}` : ''} page={sel?.new_page ?? null} tag={newTag} />
          </div>
        )}

        {!diff && !loading && !error && (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm px-6 text-center">
            No stored comparison for the selected version pair. Choose adjacent versions (each historical AusPAR is compared against the next).
          </div>
        )}
      </div>
    </div>
  );
}
